import { useState, useEffect, useCallback, useRef } from 'react';
import { VISUAL_METRIC_DEFAULT_MAPPING } from '../const';

interface UseVisualMetricsProps {
  activeSegment?: string;
  selectedMetrics?: string[];
  onMetricChange?: (metric: string) => void;
}

interface UseVisualMetricsReturn {
  visualMetricValue: string;
  visualMetricValues: string[];
  setVisualMetricValue: (value: string) => void;
  setVisualMetricValues: (values: string[]) => void;
  handleDualMetricChange: (values: string[]) => void;
  handleMetricSelectChange: (value: string, needUpdateUrl?: boolean) => void;
}

/**
 * Custom hook to manage visual metrics state and synchronization
 */
const useVisualMetrics = ({
  activeSegment,
  selectedMetrics,
  onMetricChange,
}: UseVisualMetricsProps): UseVisualMetricsReturn => {
  const [visualMetricValue, setVisualMetricValue] = useState<string>('');
  const [visualMetricValues, setVisualMetricValues] = useState<string[]>([]);
  
  const prevSelectedMetricsRef = useRef<string[]>();
  const prevActiveSegmentRef = useRef<string>();

  /**
   * Configure visual metric values based on selected metrics
   */
  const configureVisualMetricValues = useCallback((
    selectedMetrics: string[],
    currentValues: string[]
  ): string[] => {
    if (!selectedMetrics?.length) return currentValues;
    
    const validValues = currentValues.filter((value) => selectedMetrics.includes(value));
    
    // If current values are still valid, keep them (up to 2)
    if (validValues.length > 0) {
      return validValues.slice(0, 2);
    }
    
    // If no valid values, use the first selected metric
    return [selectedMetrics[0]];
  }, []);

  /**
   * Update visual metric value and notify parent
   */
  const updateVisualMetricValue = useCallback((newValue: string) => {
    if (newValue !== visualMetricValue) {
      setVisualMetricValue(newValue);
      onMetricChange?.(newValue);
    }
  }, [visualMetricValue, onMetricChange]);

  /**
   * Main effect to handle all state synchronization
   */
  useEffect(() => {
    const hasActiveSegmentChanged = activeSegment !== prevActiveSegmentRef.current;
    const hasSelectedMetricsChanged = JSON.stringify(selectedMetrics) !== JSON.stringify(prevSelectedMetricsRef.current);
    
    // Handle active segment changes
    if (activeSegment && hasActiveSegmentChanged) {
      const defaultValues = VISUAL_METRIC_DEFAULT_MAPPING[activeSegment] || [];
      if (Array.isArray(defaultValues) && defaultValues.length > 0) {
        setVisualMetricValues(defaultValues);
        if (defaultValues[0]) {
          updateVisualMetricValue(defaultValues[0]);
        }
      }
      prevActiveSegmentRef.current = activeSegment;
    }
    
    // Handle selected metrics changes
    if (selectedMetrics?.length && hasSelectedMetricsChanged) {
      const newVisualMetricValues = configureVisualMetricValues(selectedMetrics, visualMetricValues);
      
      // Only update if values actually changed
      if (JSON.stringify(newVisualMetricValues) !== JSON.stringify(visualMetricValues)) {
        setVisualMetricValues(newVisualMetricValues);
        
        // Update active metric if needed
        if (newVisualMetricValues[0]) {
          updateVisualMetricValue(newVisualMetricValues[0]);
        }
      }
      
      prevSelectedMetricsRef.current = selectedMetrics;
    }
  }, [activeSegment, selectedMetrics, visualMetricValues, configureVisualMetricValues, updateVisualMetricValue]);

  /**
   * Handle dual selector changes
   */
  const handleDualMetricChange = useCallback((values: string[]) => {
    const next = (values || []).slice(0, 2);
    setVisualMetricValues(next);
    if (next[0]) {
      updateVisualMetricValue(next[0]);
    }
  }, [updateVisualMetricValue]);

  /**
   * Handle single metric selector changes
   */
  const handleMetricSelectChange = useCallback((value: string, needUpdateUrl = true) => {
    updateVisualMetricValue(value);
    setVisualMetricValues((prev) => {
      const rest = (prev || []).filter((v) => v !== value);
      return [value, ...rest].slice(0, 2);
    });
  }, [updateVisualMetricValue]);

  return {
    visualMetricValue,
    visualMetricValues,
    setVisualMetricValue,
    setVisualMetricValues,
    handleDualMetricChange,
    handleMetricSelectChange,
  };
};

export default useVisualMetrics;